import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trackCustomSizeEntered } from "@/lib/analytics/segment";
import { deferredFocusRef } from "@/lib/dom";
import { getSizeConstraints } from "@/lib/product/size";
import { useProduct } from "@/queries/useProduct";
import { usePdpStore } from "@/stores/usePdpStore";
import ApplyButton from "./ApplyButton";
import { getCurrentDimensionError, resolveDisplayError, truncateToThreeDecimals } from "./customSizeHelpers";
import { useProductBuilderContext } from "./ProductBuilderContext";
import RequiredIndicator from "./RequiredIndicator";
interface Props {
  initialWidth?: string;
  initialHeight?: string;
  autoFocus?: boolean;
  onApplied?: (width: string, height: string) => void;
}
function getSizeDescribedBy(error: string | undefined): string {
  return error ? "custom-size-hint size-error" : "custom-size-hint";
}
export default function CustomSize(t0) {
  const $ = _c(29);
  const {
    initialWidth,
    initialHeight,
    autoFocus: t1,
    onApplied
  } = t0;
  const autoFocus = t1 === undefined ? false : t1;
  const {
    product
  } = useProductBuilderContext();
  const {
    data: productAdmin
  } = useProduct(product.sku);
  const selectedMaterial = usePdpStore(_temp);
  const storeWidth = usePdpStore(_temp2);
  const storeHeight = usePdpStore(_temp3);
  const storeSizeError = usePdpStore(_temp4);
  const selectSize = usePdpStore(_temp5);
  const clearValidationError = usePdpStore(_temp6);
  const materialValue = selectedMaterial ?? productAdmin?.defaultMaterial;
  const materialOption = productAdmin?.materialOptions.find(mat => mat.sku === materialValue);
  const constraints = getSizeConstraints(materialOption, productAdmin);
  const hasPrefill = !!initialWidth && !!initialHeight;
  const [localWidth, setLocalWidth] = useState(initialWidth ?? "");
  const [localHeight, setLocalHeight] = useState(initialHeight ?? "");
  const [showErrors, setShowErrors] = useState(hasPrefill);
  const maxShort = Math.min(constraints.maxWidth, constraints.maxHeight);
  const maxLong = Math.max(constraints.maxWidth, constraints.maxHeight);
  const {
    displayError,
    isApplied
  } = resolveDisplayError({
    showErrors,
    localWidth,
    localHeight,
    storeWidth,
    storeHeight,
    constraints,
    storeSizeError
  });
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function handleChange(setLocal, value) {
      setLocal(truncateToThreeDecimals(value));
      setShowErrors(false);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const handleChange = t2;
  const validate = function validate() {
    if (!localWidth.trim() || !localHeight.trim()) {
      return;
    }
    setShowErrors(true);
    if (getCurrentDimensionError(localWidth, localHeight, constraints)) {
      return;
    }
    const trimmedWidth = truncateToThreeDecimals(localWidth);
    const trimmedHeight = truncateToThreeDecimals(localHeight);
    setLocalWidth(trimmedWidth);
    setLocalHeight(trimmedHeight);
    selectSize(trimmedWidth, trimmedHeight);
    clearValidationError("size");
    trackCustomSizeEntered(trimmedWidth, trimmedHeight);
    onApplied?.(trimmedWidth, trimmedHeight);
  };
  let t3;
  if ($[1] !== maxLong || $[2] !== maxShort) {
    t3 = <p id="custom-size-hint" className="text-sm leading-5 text-muted-foreground">We can print any sizes up to {maxShort}" × {maxLong}"</p>;
    $[1] = maxLong;
    $[2] = maxShort;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Label htmlFor="custom-width" className="text-sm leading-5">Width<RequiredIndicator /></Label>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== handleChange) {
    t5 = e_0 => handleChange(setLocalWidth, e_0.target.value);
    $[5] = handleChange;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="mt-6 shrink-0 text-lg text-muted-foreground">×</span>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = "flex flex-col gap-1";
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Label htmlFor="custom-height" className="text-sm leading-5">Height<RequiredIndicator /></Label>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const T0 = Input;
  const t9 = "custom-height";
  const t10 = "decimal";
  const t11 = !!displayError || undefined;
  const t12 = getSizeDescribedBy(displayError);
  let t13;
  if ($[9] !== handleChange || $[10] !== setLocalHeight) {
    t13 = e_1 => handleChange(setLocalHeight, e_1.target.value);
    $[9] = handleChange;
    $[10] = setLocalHeight;
    $[11] = t13;
  } else {
    t13 = $[11];
  }
  let t14;
  if ($[12] !== T0 || $[13] !== localHeight || $[14] !== t11 || $[15] !== t12 || $[16] !== t13) {
    t14 = <T0 id={t9} inputMode={t10} aria-invalid={t11} aria-describedby={t12} value={localHeight} onChange={t13} className="w-24 text-center text-sm" />;
    $[12] = T0;
    $[13] = localHeight;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== t14 || $[19] !== t8) {
    t15 = <div className={t7}>{t8}{t14}</div>;
    $[18] = t14;
    $[19] = t8;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== localHeight || $[22] !== localWidth) {
    t16 = !localWidth.trim() || !localHeight.trim();
    $[21] = localHeight;
    $[22] = localWidth;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== isApplied || $[25] !== t16) {
    t17 = <ApplyButton isApplied={isApplied} disabled={t16} className="mt-6" />;
    $[24] = isApplied;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== displayError) {
    t18 = displayError && <p id="size-error" role="alert" className="text-sm text-destructive">{displayError}</p>;
    $[27] = displayError;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  return <div className="flex flex-col gap-3 pt-3">{t3}<form onSubmit={e => {
      e.preventDefault();
      validate();
    }} className="flex flex-col gap-2"><div className="flex items-center gap-2"><div className="flex flex-col gap-1">{t4}<Input ref={autoFocus ? deferredFocusRef : undefined} id="custom-width" inputMode="decimal" aria-invalid={!!displayError || undefined} aria-describedby={getSizeDescribedBy(displayError)} value={localWidth} onChange={t5} className="w-24 text-center text-sm" /></div>{t6}{t15}{t17}</div>{t18}</form></div>;
}
function _temp6(state_4) {
  return state_4.clearValidationError;
}
function _temp5(state_3) {
  return state_3.selectSize;
}
function _temp4(state_2) {
  return state_2.validationErrors.size;
}
function _temp3(state_1) {
  return state_1.height;
}
function _temp2(state_0) {
  return state_0.width;
}
function _temp(state) {
  return state.material;
}